import type { BonusDispatchStatus } from '@/types'

interface StatusBadgeProps {
  status: BonusDispatchStatus | null | undefined
  size?: 'sm' | 'md'
  nextRetryAt?: number | null
  retryCount?: number | null
}

const STATUS_CONFIG: Record<BonusDispatchStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-600' },
  dispatched: { label: 'Dispatched', className: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepted', className: 'bg-success-100 text-success-700' },
  rejected: { label: 'Rejected', className: 'bg-danger-100 text-danger-700' },
  failed: { label: 'Failed', className: 'bg-warning-100 text-warning-700' },
  reconciled: { label: 'Reconciled', className: 'bg-purple-100 text-purple-700' },
}

export function StatusBadge({ status, size = 'sm', nextRetryAt, retryCount }: StatusBadgeProps) {
  const effectiveStatus: BonusDispatchStatus = status || 'pending'
  const config = STATUS_CONFIG[effectiveStatus]

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'

  const showRetryInfo = effectiveStatus === 'failed' && nextRetryAt
  const retryTime = nextRetryAt ? new Date(nextRetryAt * 1000) : null
  const isRetryPending = retryTime && retryTime > new Date()

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <span
        className={`inline-flex items-center rounded-full font-medium ${config.className} ${sizeClasses}`}
      >
        {config.label}
        {isRetryPending && (
          <span className="ml-1" title={`Retry #${(retryCount || 0) + 1} scheduled`}>
            ↻
          </span>
        )}
      </span>
      {showRetryInfo && isRetryPending && (
        <span className="text-xs text-gray-500">
          Retry in {formatRelativeTime(retryTime)}
        </span>
      )}
    </div>
  )
}

function formatRelativeTime(date: Date): string {
  const diffMs = date.getTime() - Date.now()
  if (diffMs < 0) return 'now'

  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return '<1m'
  if (diffMins < 60) return `${diffMins}m`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h`

  return `${Math.floor(diffHours / 24)}d`
}
